import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Space, Breadcrumb, Button, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PageHeader } from "@ant-design/pro-layout";

import type {
  ListApplicationsResponse,
  ApplicationListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { ListApplicationsRequest } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import type { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import type { GetPageCallbackFunc } from "../../components/DataTable";
import DataTable from "../../components/DataTable";
import ApplicationStore from "../../stores/ApplicationStore";
import Admin from "../../components/Admin";
import { useTitle } from "../helpers";
import { usePlanGates } from "../../contexts/PlanFeaturesContext";

interface IProps {
  tenant: Tenant;
}

function ListApplications(props: IProps) {
  useTitle("Tenants", props.tenant.getName(), "Networks");

  // Track current application count separately from the DataTable's
  // paged list so the "Add network" gate reflects the true total, not
  // just the visible page. Refetch on ApplicationStore "change" so the
  // gate updates immediately after a create/delete elsewhere — same
  // pattern TenantLoader uses for tenant edits.
  const [applicationCount, setApplicationCount] = useState<number | null>(null);
  const { features, globalAdmin, atApplicationsLimit } = usePlanGates();

  useEffect(() => {
    const fetchCount = () => {
      const req = new ListApplicationsRequest();
      req.setTenantId(props.tenant.getId());
      req.setLimit(1);
      req.setOffset(0);
      ApplicationStore.list(req, (resp: ListApplicationsResponse) => {
        setApplicationCount(resp.toObject().totalCount);
      });
    };
    ApplicationStore.on("change", fetchCount);
    fetchCount();
    return () => {
      ApplicationStore.removeListener("change", fetchCount);
    };
  }, [props.tenant]);

  // Default-deny the button while the count fetch is in flight. Otherwise
  // it renders enabled for the ~hundreds of ms before the gRPC response
  // arrives and a quick click slips past the cap. `limited` narrows the
  // default-deny to tenants that actually have a cap — admins and pre-SM
  // tenants stay unaffected. Once gateReady, we defer to the shared
  // atApplicationsLimit() policy so the rules live in one place.
  const gateReady = applicationCount !== null;
  const limited =
    !globalAdmin && features.hasPlanTags && features.maxApplications > 0;
  const atLimit = gateReady
    ? atApplicationsLimit(applicationCount!)
    : limited;
  const limitTooltip = limited
    ? `Your plan includes up to ${features.maxApplications} network${features.maxApplications === 1 ? "" : "s"}. Upgrade to add more.`
    : undefined;

  const columns: ColumnsType<ApplicationListItem.AsObject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => <Link to={`/tenants/${props.tenant.getId()}/applications/${record.id}`}>{text}</Link>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    const req = new ListApplicationsRequest();
    req.setTenantId(props.tenant.getId());
    req.setLimit(limit);
    req.setOffset(offset);

    ApplicationStore.list(req, (resp: ListApplicationsResponse) => {
      const obj = resp.toObject();
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb
            items={[
              { title: "Tenants" },
              { title: <Link to={`/tenants/${props.tenant.getId()}`}>{props.tenant.getName()}</Link> },
              { title: "Networks" },
            ]}
          />
        )}
        title="Networks"
        extra={[
          <Admin tenantId={props.tenant.getId()} isDeviceAdmin key="add-application">
            {atLimit ? (
              <Tooltip title={limitTooltip}>
                {/* Disabled Ant Button swallows pointer events, so the
                    Link isn't reachable even if someone hacks it back in.
                    Tooltip explains why the button's dead. */}
                <Button type="primary" disabled>
                  Add network
                </Button>
              </Tooltip>
            ) : (
              <Button type="primary">
                <Link to={`/tenants/${props.tenant.getId()}/applications/create`}>Add network</Link>
              </Button>
            )}
          </Admin>,
        ]}
      />
      <DataTable columns={columns} getPage={getPage} rowKey="id" />
    </Space>
  );
}

export default ListApplications;
